// Los productos reales no guardan emoji ni color: la grilla los deriva de la
// categoría para conservar el aspecto de la demo. Es decoración, nunca un dato
// de negocio.

const PALETA = [
  "#2C86C5",
  "#B27835",
  "#C84A2F",
  "#7A934C",
  "#8B5E3C",
  "#D5046D",
  "#9B6444",
  "#F59B24",
  "#2C9A83",
  "#6B5BA5",
];

const EMOJI_POR_CATEGORIA = {
  cafeteria: "☕",
  "cafes especiales": "☕",
  aguas: "💧",
  gaseosas: "🥤",
  "jugos y licuados": "🧃",
  pasteleria: "🍰",
  panaderia: "🥐",
  sandwiches: "🥪",
  desayunos: "🍳",
  postres: "🍮",
  "libre de gluten": "🌾",
  "take away cafeteria": "🥡",
  "menu universitario": "🍽️",
};

export const normalizarCategoria = (valor) =>
  String(valor || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export const getEmojiCategoria = (categoria) =>
  EMOJI_POR_CATEGORIA[normalizarCategoria(categoria)] || "🍴";

export const getAccentCategoria = (categoria) => {
  const clave = normalizarCategoria(categoria);
  if (!clave) return PALETA[0];
  let hash = 0;
  for (let i = 0; i < clave.length; i += 1) {
    hash = (hash * 31 + clave.charCodeAt(i)) % 100000;
  }
  return PALETA[hash % PALETA.length];
};

// Traduce el catálogo autoritativo del backend a la forma que consumen los
// componentes de la pantalla.
export const mapProductoCatalogo = (producto) => ({
  id: Number(producto.idProducto),
  name: producto.nombre,
  category: producto.categoria,
  price: Number(producto.precio || 0),
  precioCarta: Number(producto.precioCarta || 0),
  precioEvento: producto.precioEvento === null ? null : Number(producto.precioEvento),
  esElaborado: Boolean(producto.esElaborado),
  emoji: getEmojiCategoria(producto.categoria),
  accent: getAccentCategoria(producto.categoria),
});

const formatearPesos = (valor) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 0, maximumFractionDigits: 2 })
    .format(Number(valor || 0));

const describirPieza = (voucher) => {
  const valor = Number(voucher.valorPieza || 0);
  if (voucher.tipo === "MONTO") return formatearPesos(valor);
  if (voucher.tipo === "PRODUCTO") return `${valor} × ${voucher.producto || "producto"}`;
  return `${valor} × ${voucher.categoria || "categoría"}`;
};

// Un voucher físico llega como tanda (presupuesto) con el valor de una pieza.
// Se traduce a la forma que ya usaban los componentes de la demo: `units` y
// `amount` describen lo que cubre UN papel, que es lo que declara el vendedor.
export const mapVoucherFisico = (voucher) => ({
  code: String(voucher.idVoucher),
  id: Number(voucher.idVoucher),
  name: `${voucher.grupo || "Voucher"} · ${describirPieza(voucher)}`,
  type: voucher.tipo,
  wholePaper: true,
  productId: voucher.idProducto ? Number(voucher.idProducto) : null,
  category: voucher.categoria || null,
  units: voucher.tipo === "MONTO" ? null : Number(voucher.valorPieza || 0),
  amount: voucher.tipo === "MONTO" ? Number(voucher.valorPieza || 0) : null,
  presupuesto: Number(voucher.presupuesto || 0),
  saldo: voucher.saldo == null ? null : Number(voucher.saldo),
  saldoDiario: voucher.saldoDiario == null ? null : Number(voucher.saldoDiario),
});

const ICONO_POR_MEDIO = {
  TRANSFERENCIA: "account_balance",
  EFECTIVO: "payments",
  POSNET: "credit_card",
  QR: "qr_code_2",
};

export const getIconoMedioPago = (codigo) =>
  ICONO_POR_MEDIO[String(codigo || "").toUpperCase()] || "payments";

// Traduce una operación del evento a la forma que muestra el diálogo de ventas
// recientes, que nació con los datos del mock.
export const mapVentaReciente = (venta) => ({
  id: venta.nroPedido ? `EV-${venta.nroPedido}` : `EV-${venta.idPedido}`,
  // Con esto se piden los documentos de impresión al reimprimir.
  idPedido: venta.idPedido,
  time: venta.creadaEl
    ? new Date(String(venta.creadaEl).replace(" ", "T")).toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "—",
  customer: venta.idCliente ? `Cliente #${venta.idCliente}` : "Consumidor final",
  items: Number(venta.unidades || 0),
  payment: venta.medioPago || (Number(venta.total || 0) <= 0.01 ? "No requerido" : "—"),
  benefit: venta.voucher || null,
  discount: Number(venta.descuento || 0),
  total: Number(venta.total || 0),
  status: venta.anulada ? "Anulado" : "Aprobado",
});

export const mapComprador = (comprador) => {
  if (!comprador?.encontrado) {
    return {
      id: null,
      dni: comprador?.dni || "",
      firstName: "",
      lastName: "",
      communityRole: "",
      isCommunityMember: false,
      coincidencias: comprador?.coincidencias || 0,
    };
  }
  return {
    id: comprador.idCliente,
    dni: comprador.dni,
    firstName: comprador.nombre || "",
    lastName: comprador.apellido || "",
    communityRole: comprador.appUsuario || "",
    isCommunityMember: Boolean(comprador.esComunidadUpro),
    coincidencias: comprador.coincidencias || 0,
  };
};

const normalizarIdentidad = (value) => String(value || "").trim().toLowerCase();
const normalizarDni = (value) => String(value || "").replace(/\D/g, "");

// searchUnifiedUsers tambien busca parcialmente por nombre, correo y DNI. En
// este punto de venta solo aceptamos coincidencias exactas del documento y
// conservamos usuarios distintos aunque compartan DNI entre aplicaciones.
export const filtrarUsuariosUnificadosPorDni = (usuarios = [], rawDni = "") => {
  const dni = normalizarDni(rawDni);
  if (!dni) return [];

  return usuarios.filter((usuario, index, self) => {
    if (normalizarDni(usuario?.dni) !== dni) return false;

    const clave = [
      normalizarIdentidad(usuario?.appUsuario || usuario?.tipoApp),
      normalizarIdentidad(usuario?.id),
      normalizarIdentidad(usuario?.tipoUser || usuario?.tipoUsuario),
      normalizarIdentidad(usuario?.email),
      normalizarIdentidad(usuario?.username),
      dni,
    ].join("|");

    return index === self.findIndex((item) => {
      if (normalizarDni(item?.dni) !== dni) return false;
      return [
        normalizarIdentidad(item?.appUsuario || item?.tipoApp),
        normalizarIdentidad(item?.id),
        normalizarIdentidad(item?.tipoUser || item?.tipoUsuario),
        normalizarIdentidad(item?.email),
        normalizarIdentidad(item?.username),
        dni,
      ].join("|") === clave;
    });
  });
};

export const mapUsuarioUnificadoComprador = (usuario) => ({
  id: null,
  externalId: usuario?.id || null,
  dni: normalizarDni(usuario?.dni),
  firstName: usuario?.nombre || "",
  lastName: usuario?.apellido || "",
  communityRole: usuario?.appUsuario || usuario?.tipoApp || "",
  appUsuario: usuario?.appUsuario || usuario?.tipoApp || "",
  tipoUsuario: usuario?.tipoUser || usuario?.tipoUsuario || "Usuario",
  isCommunityMember: true,
});

// La API verifica esta referencia en el origen. No se envian nombre, tipo,
// beneficios ni credenciales del comprador para decidir la tarifa.
export const referenciaCompradorEvento = (comprador) => comprador?.externalId && comprador?.appUsuario
  ? { idUsuario: String(comprador.externalId), appUsuario: comprador.appUsuario, dni: comprador.dni }
  : null;
